// fetchManagementCompany.jsx - Management Company API functions
import React from "react";
import { authenticatedFetchWithRefresh, enhancedTokenManager } from '../utils/enhancedTokenManager';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Helper function for non-authenticated requests
const publicFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
    return response;
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Get list of management companies by building ID
 * @param {number} buildingId - Building ID
 * @returns {Promise<Array>} - Array of management companies
 */
// Update your getListManagementCompanies function with debugging

export const getListManagementCompanies = async (buildingId) => {
  try {

    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/ManagementCompany/GetListManagmentByBuildingId`, 
      {
        method: 'POST',
        body: JSON.stringify({ buildingId: buildingId })
      }
    );


    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    
    if (Array.isArray(data)) {
      
    } else {
    }

    return data;
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Get management company by ID
 * @param {number} companyId - Management Company ID
 * @returns {Promise<Object>} - Management company data
 */
export const getManagementCompanyById = async (companyId) => {
  try {

    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/ManagementCompany/GetManagmentByBuildingId`,
      {
        method: 'POST',
        body: JSON.stringify({ managementCompanyId: companyId })
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'GetManagementCompanyById failed');
    }

    return await response.json();
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Create a new management company
 * @param {Object} companyData - Management company data
 * @returns {Promise<Object>} - API response
 */
export const createManagementCompany = async (companyData) => {
  try {
    // Debug: Log the exact data being sent
    
    // Check for required fields
    const requiredFields = ['Name', 'BuildingID'];
    const missingFields = requiredFields.filter(field => !companyData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }
    
    // Clean the data - remove null/undefined/empty fields except where needed
    const cleanedData = Object.keys(companyData).reduce((acc, key) => {
      if (companyData[key] !== null && companyData[key] !== undefined && companyData[key] !== '') {
        acc[key] = companyData[key];
      } else if (requiredFields.includes(key)) {
        // Keep required fields even if empty (let server handle validation)
        acc[key] = companyData[key];
      }
      return acc;
    }, {});
    
    
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/ManagementCompany/AddManagment`, {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      
      // Try to parse error as JSON if possible
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(`שגיאת שרת: ${errorJson.message || errorText}`);
      } catch (parseError) {
        throw new Error(`שגיאת שרת: ${errorText}`);
      }
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result;
    
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Update an existing management company
 * @param {Object} companyData - Management company data to update
 * @returns {Promise<Object>} - API response
 */
export const updateManagementCompany = async (companyData) => {
  try {

    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/ManagementCompany/UpdateManagment`, {
      method: 'POST',
      body: JSON.stringify(companyData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Delete a management company
 * @param {number} companyId - Management Company ID to delete
 * @returns {Promise<Object>} - API response
 */
export const deleteManagementCompany = async (companyId) => {
  try {
    
    const response = await authenticatedFetchWithRefresh(`${API_URL}/api/ManagementCompany/DeleteManagment`, {
      method: 'POST',
      body: JSON.stringify({ ManagementCompanyID: companyId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || 'Failed to delete management company');
      } catch (parseError) {
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    // If no JSON content, return success indicator
    return { success: true, message: 'Management company deleted successfully' };
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

// Helper function to handle API responses
const handleResponse = async (response) => {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return await response.json();
    }
    
    return null;
};

/**
 * Get management companies with advanced filtering and pagination
 * @param {Object} params - Filter parameters
 * @param {number} params.buildingId - Building ID
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 10)
 * @param {string} params.search - Search term (default: '')
 * @param {string} params.sortBy - Sort field (default: 'Name')
 * @param {string} params.sortOrder - Sort order 'asc' or 'desc' (default: 'asc')
 * @returns {Promise<Object>} - Paginated management companies
 */
export const getManagementCompaniesWithFilters = async (params = {}) => {
  try {
    const {
      buildingId,
      page = 1,
      pageSize = 10,
      search = '',
      sortBy = 'Name',
      sortOrder = 'asc'
    } = params;

    if (!buildingId) {
      throw new Error('Building ID is required');
    }

    const requestBody = {
      buildingId,
      page,
      pageSize,
      search,
      sortBy,
      sortOrder
    };


    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/ManagementCompany/GetManagementCompaniesWithFilters`,
      {
        method: 'POST',
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch management companies with filters');
    }

    return await response.json();
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Bulk operations for management companies
 * @param {Object} operationData - Bulk operation data
 * @param {string} operationData.operation - Operation type ('delete', 'update')
 * @param {Array} operationData.companyIds - Array of company IDs
 * @param {Object} operationData.updateData - Data to update (for update operation)
 * @returns {Promise<Object>} - API response
 */
export const bulkManagementCompanyOperations = async (operationData) => {
  try {

    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/ManagementCompany/BulkOperations`,
      {
        method: 'POST',
        body: JSON.stringify(operationData)
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Bulk operation failed');
    }

    return await response.json();
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Validate management company data before submission
 * @param {Object} companyData - Management company data to validate
 * @returns {Object} - Validation result with errors array
 */
export const validateManagementCompanyData = (companyData) => {
  const errors = [];
  
  // Required fields validation
  if (!companyData.Name || companyData.Name.trim().length === 0) {
    errors.push('שם החברה הוא שדה חובה');
  }

  if (!companyData.BuildingID) {
    errors.push('מזהה בניין הוא שדה חובה');
  }

  // Email validation (if provided)
  if (companyData.Email && companyData.Email.trim().length > 0) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(companyData.Email)) {
      errors.push('כתובת דוא"ל לא תקינה');
    }
  }

  // Phone validation (if provided)
  if (companyData.Phone && companyData.Phone.trim().length > 0) {
    const phoneRegex = /^[\d\-\+\(\)\s]+$/;
    if (!phoneRegex.test(companyData.Phone)) {
      errors.push('מספר טלפון לא תקין');
    }
  }

  // Name length validation
  if (companyData.Name && companyData.Name.length > 100) {
    errors.push('שם החברה לא יכול להיות ארוך מ-100 תווים');
  }

  // Contact person length validation
  if (companyData.ContactPerson && companyData.ContactPerson.length > 100) {
    errors.push('שם איש הקשר לא יכול להיות ארוך מ-100 תווים');
  }

  // Email length validation
  if (companyData.Email && companyData.Email.length > 255) {
    errors.push('כתובת דוא"ל לא יכולה להיות ארוכה מ-255 תווים');
  }

  // Phone length validation
  if (companyData.Phone && companyData.Phone.length > 20) {
    errors.push('מספר טלפון לא יכול להיות ארוך מ-20 תווים');
  }

  // Address length validation
  if (companyData.Address && companyData.Address.length > 500) {
    errors.push('כתובת לא יכולה להיות ארוכה מ-500 תווים');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Export management companies data to CSV
 * @param {number} buildingId - Building ID
 * @returns {Promise<Blob>} - CSV file blob
 */
export const exportManagementCompaniesToCSV = async (buildingId) => {
  try {

    const response = await authenticatedFetchWithRefresh(
      `${API_URL}/api/ManagementCompany/ExportToCSV`,
      {
        method: 'POST',
        body: JSON.stringify({ buildingId })
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to export management companies');
    }

    return await response.blob();
  } catch (error) {
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.name === 'TypeError') {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
};

/**
 * Check if current token is valid and not expired
 * @returns {boolean} - True if token is valid
 */
export const isTokenValid = () => {
  const token = enhancedTokenManager.getToken();
  return token && !enhancedTokenManager.isTokenExpired(token);
};

/**
 * Get token expiration info
 * @returns {Object|null} - Token expiration details or null if no token
 */
export const getTokenInfo = () => {
  const token = enhancedTokenManager.getToken();
  if (!token) return null;

  try {
    const payload = enhancedTokenManager.decodeToken(token);
    if (!payload) return null;

    const expiryDate = new Date(payload.exp * 1000);
    const currentDate = new Date();
    const timeRemaining = expiryDate.getTime() - currentDate.getTime();

    return {
      expiryDate,
      currentDate,
      timeRemainingMinutes: Math.floor(timeRemaining / 60000),
      timeRemainingHours: Math.floor(timeRemaining / 3600000),
      isExpired: timeRemaining <= 0,
      shouldRefresh: enhancedTokenManager.shouldRefresh(token)
    };
  } catch (error) {
    return null;
  }
};